import { useEffect, useRef, useCallback } from 'react';
import { useStore, useStorePrimitive } from './useStore';
import { useBootstrapContext } from './bootstrap/BootstrapProvider';
import { api } from '../services/api';
import { logger } from '../services/logger.client';
import type { CombatState } from '../state/combatActions';
import type { Combatant } from '../types/types';

/**
 * Debounce delay for combat state synchronization in milliseconds.
 * Per ADR-033, this prevents spamming the API during rapid state changes
 * (e.g., HP adjustments, initiative rolls).
 */
const COMBAT_SYNC_DEBOUNCE_MS = 500;

/**
 * Type for the serialized combat state payload sent to the API.
 * Per ADR-033, this includes the full combat state: metadata and combatants.
 * The endpoint is PUT /api/combat-state/:campaignId.
 */
interface CombatStatePayload {
  round: number;
  turn: number;
  isActive: boolean;
  setupMode: boolean;
  combatants: Combatant[];
}

/**
 * Serializes combat state for comparison and API transmission.
 * Creates a stable JSON string that can be compared to detect actual changes.
 * Per ADR-033, the full combat state (including combatants) is synced.
 *
 * @param combatState - The combat state from the Zustand store
 * @returns A JSON string representation of the full combat state
 */
function serializeCombatState(combatState: CombatState): string {
  const payload: CombatStatePayload = {
    round: combatState.round,
    turn: combatState.turn,
    isActive: combatState.isActive,
    setupMode: combatState.setupMode,
    combatants: combatState.combatants,
  };
  return JSON.stringify(payload);
}

/**
 * Hook that syncs combat state metadata to the database with 500ms debounce.
 *
 * This hook monitors changes to combat state (combatants, round, turn, isActive, setupMode)
 * and persists them to the backend via the PUT /api/combat-state/:campaignId endpoint (ADR-033).
 *
 * Architecture decisions:
 * - ADR-033: Phase 3 combat persistence architecture
 * - 500ms debounce prevents API spam during rapid changes
 * - Only syncs when activeSessionId and campaignId are present
 * - Fails silently to maintain UI responsiveness (combat state in memory)
 * - Full combat state (including combatants) is synced per ADR-033
 *
 * @remarks
 * The hook uses JSON serialization to detect actual state changes,
 * preventing unnecessary API calls when state object reference changes
 * but values remain the same (common with Immer-based state updates).
 *
 * @example
 * ```tsx
 * // In App.tsx or a combat-related container
 * function App() {
 *   useCombatSync(); // Automatically syncs combat state changes
 *   // ... rest of app
 * }
 * ```
 */
export function useCombatSync(): void {
  // Select combat state using shallow equality (ADR-001 compliant)
  const combatState = useStore(s => s.session?.combat);
  // Select primitive activeSessionId without shallow (performance optimization)
  const activeSessionId = useStorePrimitive(s => s.session?.general?.activeSessionId);
  // Select activeCampaignId for the ADR-033 endpoint: PUT /api/combat-state/:campaignId
  const campaignId = useStorePrimitive(s => s.session?.general?.activeCampaignId);

  // Get bootstrap completion status to avoid syncing before hydration
  // CRITICAL: Without this check, the hook may sync the default Zustand state
  // (setupMode: true, isActive: false) before the bootstrap hydrates the store
  // with the actual server state, effectively overwriting persisted combat state.
  const { state: bootstrapState } = useBootstrapContext();
  const isBootstrapComplete = bootstrapState.hasCriticalCompleted;

  // Refs for debounce management
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track the last synced state to avoid redundant API calls
  const lastSyncedStateRef = useRef<string | null>(null);
  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);

  /**
   * Performs the actual API call to sync combat state.
   * This is separated from the debounce logic for clarity and testability.
   *
   * @param state - The combat state to sync
   * @param targetCampaignId - The campaign ID for the endpoint URL
   */
  const syncToBackend = useCallback(async (state: CombatState, targetCampaignId: number): Promise<void> => {
    const payload: CombatStatePayload = {
      round: state.round,
      turn: state.turn,
      isActive: state.isActive,
      setupMode: state.setupMode,
      combatants: state.combatants,
    };

    try {
      await api(`/api/combat-state/${targetCampaignId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });

      // Update the last synced state on success
      if (isMountedRef.current) {
        lastSyncedStateRef.current = serializeCombatState(state);
      }

      logger.debug(
        { campaignId: targetCampaignId, round: payload.round, turn: payload.turn, isActive: payload.isActive },
        'useCombatSync: Combat state synced to backend.'
      );
    } catch (error) {
      // Log the error but don't throw - combat state is still in memory
      // and the UI remains responsive. The sync will be retried on the
      // next state change.
      logger.error(
        { err: error, campaignId: targetCampaignId },
        'useCombatSync: Failed to sync combat state to backend.'
      );
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    // CRITICAL: Skip sync until bootstrap is complete.
    // This prevents overwriting server state with default Zustand values during
    // the window between page load and bootstrap hydration. Without this check,
    // the default state (setupMode: true, isActive: false) would be synced to
    // the server, destroying any active combat session. See ADR-033.
    if (!isBootstrapComplete) {
      logger.debug(
        { isBootstrapComplete },
        'useCombatSync: Bootstrap not complete, skipping sync to prevent overwriting server state.'
      );
      return;
    }

    // Skip sync if no active session - combat state won't be persisted
    // without a valid session context.
    if (!activeSessionId) {
      logger.debug(
        { activeSessionId },
        'useCombatSync: No active session, skipping sync.'
      );
      return;
    }

    // Skip sync if no campaign is selected - the ADR-033 endpoint requires
    // a valid campaignId in the URL path.
    if (!campaignId) {
      logger.debug(
        { campaignId },
        'useCombatSync: No campaign selected, skipping sync.'
      );
      return;
    }

    // Skip if combat state is not initialized (should not happen normally,
    // but defensive check for edge cases during bootstrap).
    if (!combatState) {
      logger.debug('useCombatSync: Combat state is null/undefined, skipping sync.');
      return;
    }

    // Serialize current state to compare with last synced state
    const currentSerializedState = serializeCombatState(combatState);

    // Skip if the state hasn't actually changed (same values, different reference).
    // This is important because Immer creates new object references even when
    // values don't change, which would otherwise trigger unnecessary API calls.
    if (currentSerializedState === lastSyncedStateRef.current) {
      logger.debug('useCombatSync: State unchanged, skipping sync.');
      return;
    }

    // Clear any existing timeout to implement debounce
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Capture campaignId in closure for the timeout callback
    const currentCampaignId = campaignId;

    // Set up the debounced sync
    timeoutRef.current = setTimeout(() => {
      // Double-check mount status before async operation
      if (!isMountedRef.current) {
        return;
      }

      // Capture the current state at the time of execution
      // (not the state from when the timeout was scheduled).
      // This ensures we sync the most recent state even if multiple
      // changes occurred during the debounce period.
      void syncToBackend(combatState, currentCampaignId);
    }, COMBAT_SYNC_DEBOUNCE_MS);

    // Cleanup function: cancel pending timeout on unmount or dependency change
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [combatState, activeSessionId, campaignId, isBootstrapComplete, syncToBackend]);

  // Mark as unmounted in a separate cleanup effect to ensure
  // the flag is set even if the main effect doesn't run.
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
}

/**
 * Exported constant for testing purposes.
 * Allows tests to verify the debounce timing.
 */
export const DEBOUNCE_MS = COMBAT_SYNC_DEBOUNCE_MS;
